import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchDistressEvents, resolveDistressEvent } from "@/lib/api";
import UserBadge from "@/components/UserBadge";
import AdminToggle from "@/components/AdminToggle";
import { ChevronLeft, Shield, AlertTriangle, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminDistress() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolvingId, setResolvingId] = useState(null);

  const load = async () => {
    try {
      const r = await fetchDistressEvents({ limit: 100 });
      setData(r);
    } catch (e) {
      console.error(e);
      toast.error("Could not load distress events.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resolve = async (id) => {
    setResolvingId(id);
    try {
      await resolveDistressEvent(id);
      await load();
      toast.success("Marked resolved.");
    } catch (e) {
      toast.error("Failed.");
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <div className="min-h-screen">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-obsidian-600">
        <Link to="/admin" className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-bone-100">
          <ChevronLeft className="h-3 w-3" /> Admin
        </Link>
        <div className="flex items-center gap-4">
          <AdminToggle />
          <UserBadge />
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 md:px-12 pt-12 pb-24">
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="h-5 w-5 text-terracotta" />
          <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta">Distress Events</p>
        </div>
        <h1 className="font-serif text-4xl text-bone-100 mb-2">Care log</h1>
        <p className="font-serif italic text-bone-400 mb-10">
          Moments the app surfaced crisis resources. Snippets are truncated to 120 characters.
          Emails are hashed. Retention 90 days.
        </p>

        {loading ? (
          <div className="flex items-center gap-3 text-bone-400"><Loader2 className="h-4 w-4 animate-spin" /> loading</div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 mb-8" data-testid="distress-stats">
              <div className="border border-obsidian-600 p-5">
                <p className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500 mb-2">Total events</p>
                <p className="font-serif text-3xl text-bone-100">{data.total}</p>
              </div>
              <div className="border border-obsidian-600 p-5">
                <p className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500 mb-2">Unresolved</p>
                <p className="font-serif text-3xl text-terracotta">{data.unresolved}</p>
              </div>
            </div>
            <div className="space-y-4" data-testid="distress-list">
              {data.events.length === 0 && (
                <p className="font-sans text-bone-500 text-sm">No events yet. The care log is quiet.</p>
              )}
              {data.events.map((e) => (
                <div key={e.id} className="border border-obsidian-600 p-5" data-testid={`distress-event-${e.id}`}>
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <p className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500 mb-1">
                        {new Date(e.triggered_at).toLocaleString()} · {e.section_id || "unknown"}
                      </p>
                      <p className="font-sans text-xs text-bone-400 mb-2">
                        Keywords: <span className="text-terracotta">{e.matched_keywords.join(", ")}</span>
                      </p>
                      <p className="font-serif italic text-bone-200 line-clamp-3">"{e.entry_snippet}"</p>
                    </div>
                    {!e.resolved && (
                      <button
                        data-testid={`distress-resolve-${e.id}`}
                        onClick={() => resolve(e.id)}
                        disabled={resolvingId === e.id}
                        className="inline-flex items-center gap-2 border border-terracotta text-terracotta hover:bg-terracotta hover:text-obsidian-900 px-3 py-1.5 font-sans text-[10px] uppercase tracking-[0.2em] transition-colors disabled:opacity-50"
                      >
                        {resolvingId === e.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                        Resolve
                      </button>
                    )}
                    {e.resolved && (
                      <span className="inline-flex items-center gap-2 text-bone-500 font-sans text-[10px] uppercase tracking-[0.2em]">
                        <Check className="h-3 w-3" /> Resolved
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
